/**
 * Unified Tab System - Route Tree Utilities
 * @module lib/tabs/route-utils
 */

import type { Route as TRoute } from "@tanstack/react-router";
import type { TabStaticData } from "./types";

/**
 * Normalize a route path for comparison
 */
export function normalizePath(path: string): string {
  if (!path || path === "/") return "/";
  return path.replace(/\/$/, "").replace(/^\/_app/, "");
}

/**
 * Strip the /_app prefix from a route id
 */
export function stripAppPrefix(routeId: string): string {
  return routeId.replace(/^\/_app/, "");
}

/**
 * Check if a route ID represents a dynamic route
 */
export function isDynamicRoute(routeId: string): boolean {
  return routeId.includes("/$");
}

/**
 * Extract staticData from a route.
 * TanStack Router stores staticData on the route but the type doesn't expose it directly,
 * so we use a type-agnostic approach.
 */
export function getRouteStaticData(route: Record<string, unknown>): TabStaticData | undefined {
  // Try staticData first
  if ("staticData" in route && route.staticData !== undefined) {
    return route.staticData as TabStaticData;
  }
  // Try options.staticData (some route tree shapes)
  if ("options" in route && route.options !== null && typeof route.options === "object") {
    const options = route.options as Record<string, unknown>;
    if ("staticData" in options && options.staticData !== undefined) {
      return options.staticData as TabStaticData;
    }
  }
  return undefined;
}

/**
 * Internal route record type
 */
interface RouteRecord {
  id: string;
  options?: { staticData?: TabStaticData };
  staticData?: TabStaticData;
}

/**
 * Recursively find all routes in a route tree
 */
export function findAllRoutes(route: TRoute, results: RouteRecord[] = []): RouteRecord[] {
  if (route.id) {
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const r = route as unknown as Record<string, unknown>;
    results.push({
      id: route.id,
      options: r.options as RouteRecord["options"],
      staticData: r.staticData as TabStaticData | undefined,
    });
  }

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const children = (route as any).children as TRoute[] | undefined;
  if (children) {
    for (const child of children) {
      findAllRoutes(child, results);
    }
  }

  return results;
}

/**
 * Filter routes that should appear in module tabs
 */
export function filterTabRoutes(routes: RouteRecord[], modulePath: string): RouteRecord[] {
  const normalizedModulePath = normalizePath(stripAppPrefix(modulePath));
  const moduleSegments = normalizedModulePath.split("/").filter(Boolean);

  return routes.filter(({ id }) => {
    // Skip dynamic routes
    if (isDynamicRoute(id)) return false;

    // Normalize and strip prefix
    const normalizedId = normalizePath(stripAppPrefix(id));

    // Skip the module root itself (it becomes the first tab separately)
    if (normalizedId === normalizedModulePath) return false;

    const routeSegments = normalizedId.split("/").filter(Boolean);

    // Must be deeper than the module root
    if (routeSegments.length <= moduleSegments.length) return false;

    // Must have the module as prefix
    const prefixMatch =
      routeSegments.slice(0, moduleSegments.length).join("/") === moduleSegments.join("/");

    return prefixMatch;
  });
}
